// Where the Theme Builder's preview loads the real player from.
//
// Default is the BETA channel, deliberately -- and it intentionally does
// NOT match the host used for generated client embeds.
//
// `embed/lib/SharedStringGeneratorMixin.js` moved embeds to
// `player.invintus.com` on master (Adam, 2026-06-23). Production is the
// right host to hand a client, but it is an OLDER build than beta: it
// carries neither the Shadow-DOM boundary nor the theme-token read, so a
// preview pointed there would render the player and silently ignore
// every theme value.
//
// Beta is the channel the Shadow-DOM + theme-token release soaks on, so
// it is the first host where this preview can tell the truth. Revisit
// once that release reaches production: at that point the two hosts can
// converge and this comment should go with them.
//
// The URL is passed to `public/theme-preview.html`, which loads the
// script inside the preview iframe. It is deliberately NOT bundled: the
// SDK is a third-party runtime that must stay the LIVE one, not a copy
// pinned at Control Center build time.

export const PLAYER_SDK_URL = 'https://player.beta.invintusmedia.com/app.js';

/**
 * Where an override is remembered, so it survives navigation in CC.
 *
 * sessionStorage, NOT localStorage. An override points at a build that is by
 * nature short-lived -- typically a PR deploy preview, which is torn down
 * when the PR merges. Remembered forever, it outlives its target: one set in
 * June was still pinned in August, pointing at the deploy preview for a PR
 * that had since merged, with nothing but the small "custom build" label to
 * say why the preview looked wrong. Bounding it to the tab keeps the reason
 * it is remembered at all (you set it once and keep it while you move
 * between preferences) without letting it become permanent state.
 *
 * Reading the old localStorage key is deliberately NOT carried over -- an
 * existing stale pin should lapse, which is the whole point.
 */
const OVERRIDE_KEY = 'invintus.themePreview.playerSdk';

/** `?playerSdk=<url>` to set, `?playerSdk=reset` to clear. */
const OVERRIDE_PARAM = 'playerSdk';

/**
 * The player source to preview against.
 *
 * Order: URL parameter, then a remembered override, then the beta
 * channel.
 *
 * Why an override exists. It was needed while the Shadow-DOM release was
 * still in flight: the default host rendered the player but ignored every
 * theme value, so the preview truthfully showed nothing happening, and a
 * PR deploy preview could be pointed at without shipping a temporary
 * default that would rot the moment that preview was torn down.
 *
 * That case is closed -- beta carries the release as of 2026-08-07, so the
 * default now tells the truth on its own. The override stays for the next
 * unreleased player change, but it is a development affordance, and the
 * session scoping above is what keeps it from quietly becoming permanent.
 *
 * Only absolute http(s) URLs are accepted. This value becomes a script
 * `src` inside the preview frame, so anything else is refused rather
 * than handed to the page.
 */
export function resolvePlayerSdkUrl () {
    let override = null;

    try {
        const param = new URLSearchParams(window.location.search).get(OVERRIDE_PARAM);
        if (param === 'reset') {
            window.sessionStorage.removeItem(OVERRIDE_KEY);
            // Also clear the pre-2.38 localStorage pin, so `?playerSdk=reset`
            // still means "reset" for anyone carrying one.
            window.localStorage.removeItem(OVERRIDE_KEY);
            return PLAYER_SDK_URL;
        }
        if (param) {
            if (isSafeUrl(param)) {
                window.sessionStorage.setItem(OVERRIDE_KEY, param);
                override = param;
            }
        } else {
            override = window.sessionStorage.getItem(OVERRIDE_KEY);
        }
    } catch (e) {
        // Private-mode storage or a malformed URL: fall back to default.
        return PLAYER_SDK_URL;
    }

    return override && isSafeUrl(override) ? override : PLAYER_SDK_URL;
}

/** True when this is the stock beta channel rather than an override. */
export function isDefaultPlayerSdk (url) {
    return url === PLAYER_SDK_URL;
}

function isSafeUrl (value) {
    try {
        const parsed = new URL(value, window.location.origin);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch (e) {
        return false;
    }
}
